import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaEye, FaCheck, FaSave } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import TemplatePreviewModal from "../components/TemplatePreviewModal";
import { TemplateList, selectNegotiationTemplate } from "../../../../services/Templates";

const CustomDropdownIndicator = ({ innerProps, selectProps }) => {
  const isOpen = !!(selectProps && (selectProps.menuIsOpen || selectProps.isMenuOpen));
  return (
    <div {...innerProps} className="negotiation-template-dropdown-indicator" aria-hidden>
      {isOpen ? <IoIosArrowUp className="dropdown-arrow" /> : <IoIosArrowDown className="dropdown-arrow" />}
    </div>
  );
};

const highlightText = (text = "", searchTerm = "") => {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, "gi");
  return text.split(regex).map((part, i) =>
    regex.test(part) ? <mark key={i} className="highlighted-text">{part}</mark> : part
  );
};

const NegotiationStep2 = ({ formObj = {}, setFormObj, nextStep, prevStep, setLoading, loading }) => {
  const navigate = useNavigate();

  const bottomButtonsRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const negotiationId = formObj?.negotiation_id;

  useEffect(() => {
    let mounted = true;

    const fetchCategories = async () => {
      try {
        const res = await TemplateList();
        const data = res?.data?.data || res?.data || res;
        if (!mounted) return;

        if (Array.isArray(data)) {
          const allTemplates = data.flatMap((c) =>
            Array.isArray(c.templates)
              ? c.templates.map((t) => ({ ...t, categoryName: c.category_name || c.label || "Uncategorized" }))
              : []
          );

          const cats = [
            {
              id: "all",
              value: "all",
              label: "All Categories",
              templates: allTemplates,
            },
            ...data.map((c) => ({
              id: c.id,
              value: c.category_name,
              label: c.category_name,
              templates: (c.templates || []).map((t) => ({
                ...t,
                categoryName: c.category_name,
              })),
            })),
          ];

          setCategories(cats);
          setSelectedCategory(cats[0]);
          setTemplates(cats[0]?.templates || []);

          if (formObj?.template_uuid && allTemplates.length) {
            const prevSel = allTemplates.find((t) => t.uuid === formObj.template_uuid);
            if (prevSel) setSelectedTemplate(prevSel);
          }
        } else {
          setCategories([]);
          setTemplates([]);
        }
      } catch (err) {
        console.error("Failed to fetch templates", err);
        toast.error("Failed to fetch templates");
      }
    };

    fetchCategories();
    return () => {
      mounted = false;
    };
  }, [formObj?.template_uuid]);


  useEffect(() => {
    if (selectedTemplate && bottomButtonsRef.current) {
      setTimeout(() => {
        bottomButtonsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
  }, [selectedTemplate]);

  const handleCategoryChange = (option) => {
    setSelectedCategory(option);
    setTemplates(option?.templates || []);
  };

  const filterTemplatesBySearch = (tpls = [], s = "") => {
    const term = (s || "").trim().toLowerCase();
    if (!term) return tpls || [];
    return (tpls || []).filter((t) => {
      const inTitle = t.title?.toLowerCase().includes(term);
      const inDesc = t.description?.toLowerCase().includes(term);
      const inTags = Array.isArray(t.tags) && t.tags.some((tag) => (tag || "").toLowerCase().includes(term));
      return inTitle || inDesc || inTags;
    });
  };

  const groupedTemplates = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory.value === "all") {
      const realCats = categories.filter((c) => c.id !== "all");
      return realCats
        .map((c) => ({
          categoryId: c.id,
          categoryLabel: c.label || c.value || "Unnamed Category",
          templates: filterTemplatesBySearch(c.templates || [], search),
        }))
        .filter((g) => g.templates.length > 0);
    }
    const filtered = filterTemplatesBySearch(selectedCategory.templates || [], search);
    return [
      {
        categoryId: selectedCategory.id,
        categoryLabel: selectedCategory.label,
        templates: filtered,
      },
    ];
  }, [categories, selectedCategory, search]);

  const handleCardClick = (template) => {
    setSelectedTemplate((prev) => (prev?.uuid === template.uuid ? null : template));
  };

  const handlePreview = (template, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (!template?.html_content) {
      toast.error("No preview content available for this template.");
      return;
    }
    setPreviewData({
      html_content: template.html_content,
      title: template.title || "Template Preview",
    });
    setShowPreviewModal(true);
  };

  const handleTemplateAction = async (e, saveOnly = false) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!selectedTemplate) return toast.error("Please select a template");
    if (!negotiationId) return toast.error("Negotiation ID missing");

    const payload = {
      negotiation_id: negotiationId,
      template_uuid: selectedTemplate.uuid,
      save_only: saveOnly,
    };

    try {
      setLoading(true);
      const res = await selectNegotiationTemplate(payload);
      const ok = !!(res?.data || res?.status === 200 || res?.ok);
      if (!ok) throw new Error("Template selection failed");


      setFormObj((prev) => ({
        ...prev,
        template_id: selectedTemplate.id,
        template_uuid: selectedTemplate.uuid,
        template_name: selectedTemplate.title,
        html_content: selectedTemplate.html_content || "",
        edited_content: "",
        draft_content: "",
      }));

      // Ab sessionStorage update karo
      if (selectedTemplate.html_content) {
        try {
          sessionStorage.setItem("template_html_content", selectedTemplate.html_content);
          sessionStorage.setItem("template_uuid", selectedTemplate.uuid);
          sessionStorage.removeItem("draft_content");
        } catch (_) { }
      }

      toast.success(saveOnly ? "Progress saved!" : "Template selected!");
      if (saveOnly) navigate("/client-portal/negotiation-contract");
      else nextStep();
    } catch (err) {
      console.error("selectNegotiationTemplate error:", err);
      toast.error(err?.message || "Failed to select template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="negotiation-container">
      <div className="negotiation-step1-header">
        <div className="negotiation-header-left">
          <h2 className="step1-title">Select Template</h2>
          <p className="step1-subtitle">Choose a template for your negotiation.</p>
        </div>
        <div className="negotiation-header-right">
          <span className="step-indicator">Step 2 of 6</span>
        </div>
      </div>

      <div className="negotiation-template-filter-container">
        <div className="negotiation-template-search-box">
          <FaSearch className="negotiation-template-search-icon" />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="negotiation-template-search-input"
          />
        </div>

        <div className="negotiation-template-dropdown">
          <Select
            options={categories}
            value={selectedCategory}
            onChange={handleCategoryChange}
            classNamePrefix="negotiation-template-select"
            isSearchable={false}
            components={{ DropdownIndicator: CustomDropdownIndicator }}
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value ?? o.id}
          />
        </div>
      </div>
      
      {selectedTemplate && (
        <div className="selected-template-indicator mb-3 p-3 rounded" style={{ background: 'rgba(15, 47, 130, 0.2)', border: '1px solid var(--blue-color)' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className="text-white-50 small d-block mb-1">Selected Template:</span>
              <h4 className="text-white mb-0 fw-bold">{selectedTemplate.title}</h4>
            </div>
            <button 
              className="btn btn-sm btn-outline-light"
              onClick={() => setSelectedTemplate(null)}
            >
              Change Template
            </button>
          </div>
        </div>
      )}

      <div>
        {groupedTemplates.length ? (
          groupedTemplates.map((group) => (
            <div key={group.categoryId}>
              <h4 className="text-white fs-3 my-3">{group.categoryLabel}</h4>
              <div className="row">
                {group.templates.map((t) => (
                  <div
                    key={t.uuid || t.id}
                    className="col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4"
                  >
                    <div
                      className={`negotiation-template-card h-100 ${selectedTemplate?.uuid === t.uuid ? "selected" : ""}`}
                      onClick={() => handleCardClick(t)}
                    >
                      {selectedTemplate?.uuid === t.uuid && (
                        <div className="selected-checkmark">
                          <FaCheck />
                        </div>
                      )}
                      <div className="template-icon-wrapper-negotiation">
                        {t.image_path ? (
                          <img src={t.image_path} alt={t.title || "template"} className="negotiation-template-img" />
                        ) : (
                          <div className="negotiation-template-img-placeholder">📄</div>
                        )}
                      </div>

                      <div className="template-content-negotiation">
                        <h5 className="template-card-title mt-3">{highlightText(t.title || "", search)}</h5>
                        <p className="template-card-description mt-2">{highlightText(t.description || "", search)}</p>
                      </div>

                      {t.tags && t.tags.length > 0 && (
                        <div className="templates__tags">
                          {t.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="templates__tag">
                              {highlightText(tag, search)}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="template-actions">
                        <button type="button" className="btn-preview-negotiation" onClick={(e) => handlePreview(t, e)}>
                          <FaEye /> Preview
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="no-templates-found text-center py-5">
            <div className="no-template-icon mb-3">
              <i className="bi bi-file-earmark-text fs-1 step2-empty-icon"></i>
            </div>
            <h5 className="fw-semibold step2-empty-title pb-3 fs-2">No templates available</h5>
            <p className="step2-empty-text mb-0">
              There are no templates under this category yet. Please try selecting a different one.
            </p>
          </div>
        )}
      </div>

      {/* Added ref={bottomButtonsRef} to this div */}
      <div className="sleeve-step-buttons" ref={bottomButtonsRef}>
        <button type="button" className="sleeve-btn-back" onClick={prevStep}>Back</button>
        <div className="sleeve-step-buttons-right">
          <button type="button" className="sleeve-btn-save" onClick={(e) => handleTemplateAction(e, true)} disabled={loading}> <FaSave /> Save & Exit</button>
          <button type="button" className="sleeve-btn-next" onClick={(e) => handleTemplateAction(e, false)} disabled={loading}> Next</button>
        </div>
      </div>

      <TemplatePreviewModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewData}
      />
    </div>
  );
};

export default NegotiationStep2;